import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Play, Square } from 'lucide-react'
import { BreathingConfig, BreathingSessionData, BreathingState } from '@/types/breathing'

interface BreathingControlsProps {
  config: BreathingConfig
  sessionData: BreathingSessionData
  breathingState: BreathingState
  onConfigChange: (config: BreathingConfig) => void
  onStart: () => void
  onStop: () => void
}

export function BreathingControls({
  config,
  sessionData,
  breathingState,
  onConfigChange,
  onStart,
  onStop
}: BreathingControlsProps) {
  const [durationInput, setDurationInput] = useState(config.duration.toString())

  const handleDurationChange = (value: string) => {
    setDurationInput(value)
    const duration = parseInt(value) || 5
    if (duration >= 1 && duration <= 60) {
      onConfigChange({
        ...config,
        duration
      })
    }
  }

  const handleStart = async () => {
    try {
      // Request fullscreen
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen()
      }
    } catch (error) {
      console.warn('Fullscreen request failed:', error)
    }
    onStart()
  }

  if (sessionData.isActive) {
    return (
      <div className="fullscreen-overlay">
        {/* Breathing subtitle */}
        <div className="breathing-subtitle visible">
          {breathingState.isInhaling ? 'Inhale' : 'Exhale'}
        </div>
        
        {/* Control overlay - minimal */}
        <div className="absolute top-4 right-4 z-20">
          <Button
            onClick={onStop}
            variant="secondary"
            size="sm"
            className="bg-black/50 hover:bg-black/70 text-white border-white/20"
          >
            <Square className="w-4 h-4 mr-2" />
            Stop
          </Button>
        </div>

        {/* Progress info */}
        <div className="absolute top-4 left-4 z-20 text-white">
          <div className="bg-black/50 rounded-lg p-3 backdrop-blur-sm">
            <div className="text-xs opacity-60 mb-1">Progress: {Math.round(breathingState.progress * 100)}%</div>
            <div className="w-32 h-2 bg-white/20 rounded-full">
              <div 
                className="h-full bg-white rounded-full transition-all duration-1000"
                style={{ width: `${breathingState.progress * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto bg-white/10 backdrop-blur-md border-white/20 text-white">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold text-white">
            Remember to Breath
          </CardTitle>
          <CardDescription className="text-white/70">
            Guide your breathing rhythm with beautiful 3D flower animations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/90">
              Duration (minutes)
            </label>
            <Input
              type="number"
              min="1"
              max="60"
              value={durationInput}
              onChange={(e) => handleDurationChange(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              placeholder="Enter duration"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-center text-sm">
            <div className="bg-blue-500/20 rounded-lg p-3">
              <div className="font-semibold text-blue-200">Inhale</div>
              <div className="text-blue-100">4 sec</div>
            </div>
            <div className="bg-green-500/20 rounded-lg p-3">
              <div className="font-semibold text-green-200">Exhale</div>
              <div className="text-green-100">6 sec</div>
            </div>
          </div>

          <Button
            onClick={handleStart}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all duration-300"
            size="lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Practice
          </Button>

          <div className="text-xs text-white/60 text-center">
            Practice in a quiet environment. Will enter fullscreen mode for the best experience
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
